import { AgGridModule } from 'ag-grid-angular';
import { AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry } from 'ag-grid-community';
import { ExcelExportModule } from 'ag-grid-enterprise';
import { MenuModule } from 'ag-grid-enterprise';
import { SetFilterModule } from 'ag-grid-enterprise';
import { StatusBarModule } from 'ag-grid-enterprise';
import { DatePipe } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, Provider } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MomentDateModule } from '@angular/material-moment-adapter';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  AuthenticationService,
  HeaderGlobalUserProfileComponent,
} from '@cdx/ngx-authentication';
import { FooterModule, HeaderComponent } from '@cdx/ngx-branding';
import {
  BrandContextComponentsModule,
  FilterChipsModule,
} from '@compumark/brand-context-components';
import { ApiModule, Configuration } from '@compumark/order-management-client';
import { TranslateModule } from '@ngx-translate/core';

import { environment } from '../environments/environment';

import { ActionBarComponent } from './action-bar/action-bar.component';
import { CancelOrderDialogComponent } from './action-items/cancel/cancel-order-dialog/cancel-order-dialog.component';
import { CancelOrderComponent } from './action-items/cancel/cancel-order/cancel-order.component';
import { ExportComponent } from './action-items/export/export.component';
import { BulkShareOrderComponent } from './action-items/share/bulk-share-order/bulk-share-order.component';
import { ShareOrderComponent } from './action-items/share/share-order/share-order.component';
import { SharedRecipientToolTipComponent } from './action-items/share/tooltip/component/shared-recipient-tool-tip/shared-recipient-tool-tip.component';
import { SharedRecipientTooltipDirective } from './action-items/share/tooltip/directive/shared-recipient-tooltip.directive';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChipsModule } from './chips/chips.module';
import { AssigneeRendererComponent } from './columns/assignee-renderer/assignee-renderer.component';
import { CheckboxRendererComponent } from './columns/checkbox-renderer/checkbox-renderer.component';
import { ClientReferenceMultiFilterComponent } from './columns/filtering/custom-filter-components/client-reference-multi-filter/client-reference-multi-filter.component';
import { SubClientReferenceMultiFilterComponent } from './columns/filtering/custom-filter-components/sub-client-reference-multi-filter/sub-client-reference-multi-filter.component';
import { FreetextIconComponent } from './columns/freetext-icon/freetext-icon.component';
import { FreetextDialogComponent } from './columns/freetext-renderer/freetext-dialog/freetext-dialog.component';
import { FreetextRendererComponent } from './columns/freetext-renderer/freetext-renderer.component';
import { GoodsAndServicesDialogComponent } from './columns/goods-and-services-renderer/goods-and-services-dialog/goods-and-services-dialog.component';
import { GoodsAndServicesRendererComponent } from './columns/goods-and-services-renderer/goods-and-services-renderer.component';
import { LogoRendererComponent } from './columns/logo-renderer/logo-renderer.component';
import { RegionRendererComponent } from './columns/register-renderer/region-renderer.component';
import { RegionsDialogComponent } from './columns/register-renderer/register-dialog/regions-dialog.component';
import { SelectAllHeaderComponent } from './columns/select-all-header/select-all-header.component';
import { TrademarkRendererComponent } from './columns/trademark-renderer/trademark-renderer.component';
import { CustomTooltipModule } from './custom-tooltip/custom-tooltip.module';
import { ExpandedImageComponent } from './expended-image/expanded-image.component';
import { OrderManagementHeaderModule } from './header/header.module';
import { MockBackendInterceptor } from './interceptors/mock-backend-interceptor';
import { MainGridComponent } from './main-grid-component/main-grid.component';
import { NavigationBarComponent } from './navigation-bar/navigation-bar.component';
import { FullTextLookupComponent } from './order/full-text/full-text-lookup/full-text-lookup.component';
import { FullTextHeaderComponent } from './order/full-text/fullText-header/full-text-header.component';
import { FullTextComponent } from './order/full-text/view-full-text/full-text.component';
import { ProductBrandInfoComponent } from './order/product-brand-info/product-brand-info.component';
import { ReferenceInfoComponent } from './order/reference-info/reference-info.component';
import { UpdateOrderDialogComponent } from './order/update/update-order-dialog/update-order-dialog.component';
import { UpdateOrderComponent } from './order/update/update-order/update-order.component';
import { WatchedRegisterComponent } from './order/watched-register/watched-register.component';
import { PreferencesModule } from './preferences/preferences.module';
import { OrdersCountMetadataResolver } from './resolvers/order-count-metadata/orders-count-metadata.resolver';
import { SecurityModule } from './security/security.module';
import { AnalyticsService } from './security/services/analytics.service';
import { HtmlSnackBarComponent } from './services/snackbar/html-snack-bar.component';
import { DeepDiffObjectPipe } from './shared/pipe/deep-diff-object.pipe';
import { MemberRecipientsComponent } from './sharing/components/member-recipients/member-recipients.component';
import { OrderSharingModalComponent } from './sharing/components/order-sharing-modal/order-sharing-modal.component';
import { SelectedOrderComponent } from './sharing/components/selected-order/selected-order.component';
import { SelectedRecipientsComponent } from './sharing/components/selected-recipients/selected-recipients.component';
import { PaginationStatusBarsComponent } from './status-bar/pagination/pagination-status-bars/pagination-status-bars.component';
import { SelectedOrderCountComponent } from './status-bar/selected-order-count/selected-order-count.component';
import { UtilModule } from './util/util.module';
import { WatchAlertModule } from './watch-alert/watch-alert.module';

ModuleRegistry.registerModules([
  AllCommunityModule,
  MenuModule,
  StatusBarModule,
  ExcelExportModule,
  SetFilterModule,
]);

const commonProviders: Provider[] = [
  { provide: 'nativeWindow', useValue: window },
  AnalyticsService,
  OrdersCountMetadataResolver,
  MatSnackBar,
  AuthenticationService,
];
const mockDataProviders: Provider[] = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: MockBackendInterceptor,
    multi: true,
  },
];

export function apiModuleConfigurationFactory(): Configuration {
  return new Configuration({
    basePath: environment.backendBaseUrl,
    withCredentials: true,
  });
}
@NgModule({
  declarations: [
    ActionBarComponent,
    AppComponent,
    AssigneeRendererComponent,
    ExpandedImageComponent,
    LogoRendererComponent,
    MainGridComponent,
    PaginationStatusBarsComponent,
    TrademarkRendererComponent,
    SelectAllHeaderComponent,
    CheckboxRendererComponent,
    UpdateOrderDialogComponent,
    WatchedRegisterComponent,
    CancelOrderComponent,
    SelectedOrderCountComponent,
    ProductBrandInfoComponent,
    CancelOrderDialogComponent,
    ReferenceInfoComponent,
    FullTextLookupComponent,
    FullTextComponent,
    FullTextHeaderComponent,
    UpdateOrderComponent,
    ExportComponent,
    RegionRendererComponent,
    RegionsDialogComponent,
    FreetextRendererComponent,
    FreetextDialogComponent,
    FreetextIconComponent,
    GoodsAndServicesRendererComponent,
    GoodsAndServicesDialogComponent,
    NavigationBarComponent,
    DeepDiffObjectPipe,
    ClientReferenceMultiFilterComponent,
    SubClientReferenceMultiFilterComponent,
    HtmlSnackBarComponent,
    ShareOrderComponent,
    SharedRecipientToolTipComponent,
    SharedRecipientTooltipDirective,
    OrderSharingModalComponent,
    MemberRecipientsComponent,
    SelectedRecipientsComponent,
    SelectedOrderComponent,
    BulkShareOrderComponent,
  ],
  imports: [
    ApiModule.forRoot(apiModuleConfigurationFactory),
    AgGridModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    CustomTooltipModule,
    FooterModule,
    HeaderComponent,
    HeaderGlobalUserProfileComponent,
    HttpClientModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    OrderManagementHeaderModule,
    SecurityModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MomentDateModule,
    MatDialogModule,
    MatTabsModule,
    MatCheckboxModule,
    MatInputModule,
    MatFormFieldModule,
    MatListModule,
    FormsModule,
    ChipsModule,
    ReactiveFormsModule,
    UtilModule,
    MatBadgeModule,
    MatSidenavModule,
    PreferencesModule,
    FilterChipsModule,
    BrandContextComponentsModule,
    MatRadioModule,
    WatchAlertModule,
    MatMenuModule,
    TranslateModule.forRoot(),
  ],
  providers: [
    DatePipe,
    ...commonProviders,
    ...(environment.mockData ? mockDataProviders : []),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
